import Skeleton from '@mui/material/Skeleton'
import {
  ColumnDef,
  ColumnFiltersState,
  ColumnOrderState,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  Updater,
  useReactTable,
  VisibilityState,
} from '@tanstack/react-table'
import { useCurrentSubscriptionData } from 'features/subscription'
import { IdEventV4 } from 'models/event'
import { useCallback, useMemo, useState } from 'react'

import { getColumnDefinitions, getRequiredFeature } from './columns'
import { CellWrapper } from './columns/CellAction'
import { hideColumnsWithoutVisibility, keepUnloadedColumns } from './columnState'
import { useClientColumnState, useColumnState } from './useColumnState'

/**
 * Data strategy for the shared events-table core.
 *
 * - `server`: server-paginated infinite query. Retains per-subscription column
 *   visibility/order persistence and feature-gated column hiding. No client sorting.
 * - `client`: in-memory array with client-side sorting and pagination. No persistence
 *   or feature gating.
 */
export type EventsTableDataStrategy =
  | {
      mode: 'server'
      /** Loaded events for the current page, or undefined while loading (renders skeletons). */
      events?: IdEventV4[]
      subscriptionId: string
    }
  | {
      mode: 'client'
      /** Fully-materialized events to render. */
      events: IdEventV4[]
      workspaceId: string
      /** Column ids that allow client-side sorting. Defaults to none. */
      enableSorting?: boolean
      pageSize?: number
      initialSorting?: SortingState
      /** Initial column visibility. Defaults to all columns visible (client has no persistence). */
      initialColumnVisibility?: VisibilityState
    }

/**
 * Headless events-table core shared by the identification visits table (server strategy)
 * and the visitor event list (client strategy). Wires `@tanstack/react-table` and, for the
 * server strategy, manages per-subscription column visibility/order persistence and
 * feature-gated columns.
 */
export function useEventsTableCore(strategy: EventsTableDataStrategy, columnDefs?: ColumnDef<IdEventV4, any>[]) {
  const { subscription } = useCurrentSubscriptionData()
  const featureSettings = subscription?.featureSettings

  const workspaceId = strategy.mode === 'server' ? strategy.subscriptionId : strategy.workspaceId
  const tableColumns = useMemo(() => columnDefs ?? getColumnDefinitions({ workspaceId }), [columnDefs, workspaceId])

  // Get all column IDs from definitions for initialization
  const allColumnIds = useMemo(() => tableColumns.map((col) => col.id as string), [tableColumns])

  // Columns whose requiredFeature flag is not enabled for this subscription.
  // Only relevant for the server strategy; client strategy does not feature-gate.
  const disabledByFeatureFlag = useMemo(
    () =>
      strategy.mode === 'server'
        ? new Set(
            tableColumns
              .filter((col) => {
                const required = getRequiredFeature(col)
                return required != null && !featureSettings?.[required]
              })
              .map((col) => col.id as string)
          )
        : new Set<string>(),
    [tableColumns, featureSettings, strategy.mode]
  )

  // Column state management — server mode uses persisted state, client mode uses local state.
  // Pass empty arrays when not in the respective mode to skip initialization work.
  const serverColumnState = useColumnState({
    subscriptionId: strategy.mode === 'server' ? strategy.subscriptionId : '',
    allColumnIds: strategy.mode === 'server' ? allColumnIds : [],
    disabledColumnIds: strategy.mode === 'server' ? disabledByFeatureFlag : new Set(),
  })

  const clientColumnState = useClientColumnState(
    strategy.mode === 'client' ? (strategy.initialColumnVisibility ?? {}) : {},
    strategy.mode === 'client' ? allColumnIds : []
  )

  // Select the appropriate column state based on mode
  const columnState = strategy.mode === 'server' ? serverColumnState : clientColumnState

  // Server mode only: columns without a stored preference (e.g. Fraud Score once enabled) default to hidden.
  const columnVisibility = useMemo(
    () =>
      strategy.mode === 'server'
        ? hideColumnsWithoutVisibility(columnState.visibility, allColumnIds)
        : columnState.visibility,
    [strategy.mode, columnState.visibility, allColumnIds]
  )

  // The stored order may keep conditional columns that aren't defined yet; expose only existing columns.
  const columnOrder = useMemo(() => {
    const existing = new Set(allColumnIds)
    const order = columnState.order.filter((id) => existing.has(id))
    return order.length === columnState.order.length ? columnState.order : order
  }, [columnState.order, allColumnIds])

  const { setOrder } = columnState
  const setColumnOrder = useCallback(
    (orderOrUpdater: Updater<ColumnOrderState>) =>
      setOrder((previous) =>
        keepUnloadedColumns(
          previous,
          typeof orderOrUpdater === 'function' ? orderOrUpdater(previous) : orderOrUpdater,
          allColumnIds
        )
      ),
    [setOrder, allColumnIds]
  )

  const [columnFilters] = useState<ColumnFiltersState>([])
  const [sorting, setSorting] = useState<SortingState>(
    strategy.mode === 'client' ? (strategy.initialSorting ?? []) : []
  )

  // Generating rows for the skeleton when data is not yet loaded (server strategy)
  const events = strategy.events
  const tableData = useMemo(
    () => (events ? events.map((eventResponse) => eventResponse) : Array(20).fill({})),
    [events]
  )

  // Overriding column rendering to show skeletons when data is not yet loaded
  const columns = useMemo(
    () =>
      events
        ? tableColumns
        : tableColumns.map((c) => ({
            ...c,
            cell: ({ row }: any) => (
              <CellWrapper>
                <Skeleton height={16} width={row.index % 2 === 0 ? '75%' : '50%'} className='transform-none!' />
              </CellWrapper>
            ),
          })),
    [tableColumns, events]
  )

  const isClient = strategy.mode === 'client'
  const enableSorting = isClient ? (strategy.enableSorting ?? false) : false
  const pageSize = isClient ? (strategy.pageSize ?? 20) : undefined

  const table = useReactTable<IdEventV4>({
    data: tableData,
    columns,
    initialState: {
      columnVisibility,
      ...(pageSize ? { pagination: { pageIndex: 0, pageSize } } : {}),
    },
    state: {
      columnVisibility,
      columnOrder,
      columnFilters,
      ...(enableSorting ? { sorting } : {}),
    },
    getRowId: (event, index) => event.event_id ?? `skeleton-${index}`,
    onColumnVisibilityChange: columnState.setVisibility,
    onColumnOrderChange: setColumnOrder,
    ...(enableSorting ? { onSortingChange: setSorting, enableSorting: true } : { enableSorting: false }),
    getCoreRowModel: getCoreRowModel(),
    ...(isClient
      ? {
          getSortedRowModel: getSortedRowModel(),
          getPaginationRowModel: getPaginationRowModel(),
        }
      : {
          manualPagination: true,
          manualFiltering: true,
        }),
  })

  return {
    table,
    columnVisibility,
    setColumnVisibility: columnState.setVisibility,
    columnOrder,
    setColumnOrder,
    disabledByFeatureFlag,
    sorting,
    setSorting,
  }
}
